import { useState } from 'react';
import { DEFAULT_TRANCHE_METHOD, tranchesTotalPct, type PaymentTranche } from '@kia/shared';
import { api } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { Button, Card, ErrorRetry, Field, Input, useToast } from '../ui';
import { ScheduleEditor, type Template } from './ScheduleEditor';

/**
 * Payment-template manager (CIRCULARITY_360 C2b) — Settings card for the reusable named payment
 * schedules that seed the deal form. Admin-only (the routes are `config.write`).
 */
export function PaymentTemplateManager() {
  const toast = useToast();
  const { data, status, error, reload } = useApiResource<Template[]>('/api/payment-templates');
  const list = data ?? [];
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [name, setName] = useState('');
  const [tranches, setTranches] = useState<PaymentTranche[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  function startNew() {
    setEditing('new');
    setName('');
    setTranches([
      {
        pct: 100,
        anchor: 'delivery',
        offset_days: 0,
        label: 'تسویه',
        method: DEFAULT_TRANCHE_METHOD,
      },
    ]);
    setIsDefault(false);
  }
  function startEdit(t: Template) {
    setEditing(t.id);
    setName(t.name);
    setTranches(t.tranches.map((x) => ({ ...x })));
    setIsDefault(t.is_default);
  }

  async function save() {
    if (!name.trim() || tranches.length === 0) {
      toast('نام و حداقل یک ردیف لازم است', 'error');
      return;
    }
    if (Math.round(tranchesTotalPct(tranches)) !== 100) {
      toast('مجموع درصدها باید ۱۰۰ شود', 'error');
      return;
    }
    setBusy(true);
    const url = editing === 'new' ? '/api/payment-templates' : `/api/payment-templates/${editing}`;
    const r = await api(url, {
      method: 'POST',
      body: JSON.stringify({ name, tranches, is_default: isDefault }),
    });
    setBusy(false);
    if (!r.ok) return toast('خطا در ذخیره', 'error');
    toast('قالب ذخیره شد ✓');
    setEditing(null);
    reload();
  }

  async function deactivate(t: Template) {
    const r = await api(`/api/payment-templates/${t.id}`, {
      method: 'POST',
      body: JSON.stringify({ active: false }),
    });
    if (r.ok) reload();
  }

  const summarize = (t: Template) =>
    t.tranches
      .map((x) => `${x.pct}٪ ${x.anchor === 'delivery' ? 'تحویل' : 'سفارش'}+${x.offset_days}`)
      .join(' · ');

  return (
    <Card title="قالب‌های زمان‌بندی پرداخت">
      <p className="muted" style={{ marginTop: 0 }}>
        شرایط پرداخت استاندارد که هنگام ثبت قرارداد قابل انتخاب‌اند و پیش‌بینی وصول را در «تقویم
        نقدینگی» می‌سازند.
      </p>

      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>نام</th>
              <th>ردیف‌ها</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td className="empty" colSpan={3}>
                  هنوز قالبی تعریف نشده — با «+ قالب جدید» بسازید.
                </td>
              </tr>
            ) : (
              list.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.name}
                    {t.is_default ? ' ★' : ''}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {summarize(t)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Button variant="secondary" onClick={() => startEdit(t)}>
                      ویرایش
                    </Button>{' '}
                    <Button variant="secondary" onClick={() => deactivate(t)}>
                      حذف
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {editing === null ? (
        <Button variant="secondary" onClick={startNew} style={{ marginTop: 8 }}>
          + قالب جدید
        </Button>
      ) : (
        <div
          style={{
            border: '1px solid var(--line, #e5e7eb)',
            borderRadius: 8,
            padding: 12,
            marginTop: 8,
          }}
        >
          <div className="row" style={{ gap: 12, alignItems: 'flex-end', marginBottom: 8 }}>
            <Field label="نام قالب">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                style={{ width: 'auto' }}
              />
              پیش‌فرض
            </label>
          </div>
          <ScheduleEditor tranches={tranches} onChange={setTranches} />
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <Button onClick={save} loading={busy}>
              ذخیره
            </Button>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>
              انصراف
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
